import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LocationPicker from "@/components/LocationPicker";
import { MapPin, Crown, Lock, ExternalLink, Edit2, CheckCircle2, AlertCircle } from "lucide-react";

export default function ZoneAndCardPanel() {
  const { user, setUser } = useAuth();
  const [zones, setZones] = useState([]);
  const [editingCard, setEditingCard] = useState(false);
  const [card, setCard] = useState({ bio: user?.bio || "", photo_url: user?.photo_url || "" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/zones").then((r) => setZones(r.data)).catch(() => {});
  }, []);

  const join = async (zoneId) => {
    setErr(""); setMsg("");
    try {
      const { data } = await api.post("/driver/join-zone", { zone_id: zoneId });
      const me = await api.get("/auth/me");
      setUser(me.data);
      setMsg(data.is_founding ? `Joined as Founding driver (slot ${data.founding_count}/7)!` : "Joined zone — subscription required to receive dispatches.");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const leave = async () => {
    try {
      await api.post("/driver/leave-zone");
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch {}
  };

  const saveCard = async () => {
    setErr(""); setMsg("");
    try {
      const { data } = await api.put("/driver/card", card);
      setMsg(`Card saved! Share at /card/${data.slug}`);
      const me = await api.get("/auth/me");
      setUser(me.data);
      setEditingCard(false);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const currentZone = zones.find((z) => z.id === user?.zone_id);

  return (
    <div className="space-y-4" data-testid="zone-card-panel">
      {/* Zone status card */}
      <div className="bg-asap-blue/30 backdrop-blur border-2 border-asap-yellow/40 p-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-asap-yellow font-bold flex items-center gap-1"><MapPin size={12}/> Service Zone</div>
        {!user?.zone_id && (
          <>
            <div className="font-display text-lg mt-1">Pick a zone to start</div>
            {zones.length === 0 && <div className="text-white/50 text-xs mt-2">No active zones yet — ask admin to create one.</div>}
            <div className="space-y-2 mt-3 max-h-60 overflow-y-auto" data-testid="zone-options">
              {zones.map((z) => (
                <button key={z.id} onClick={() => join(z.id)} data-testid={`join-zone-${z.id}`} className="w-full text-left p-3 bg-white/5 border border-asap-yellow/30 hover:bg-asap-yellow/10 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-asap-yellow">{z.name}</div>
                    <div className="text-[11px] text-white/70 mt-0.5">{z.radius_miles}mi · {z.founding_count}/7 founding {z.founding_slots_open > 0 ? "(slot open!)" : "(full — sub required)"}</div>
                  </div>
                  <span className="text-asap-yellow text-xs">Join →</span>
                </button>
              ))}
            </div>
          </>
        )}
        {user?.zone_id && currentZone && (
          <>
            <div className="font-display text-xl mt-1 text-asap-yellow">{currentZone.name}</div>
            <div className="text-xs text-white/70 mt-1">{currentZone.radius_miles}mi radius · Center {currentZone.center_lat.toFixed(3)}, {currentZone.center_lng.toFixed(3)}</div>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              {user.is_founding ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-asap-yellow text-asap-deep text-[10px] uppercase tracking-widest font-bold"><Crown size={10}/> Founding Member</span>
              ) : user.subscription_active ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500 text-white text-[10px] uppercase tracking-widest font-bold"><CheckCircle2 size={10}/> Subscribed</span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-asap-red text-white text-[10px] uppercase tracking-widest font-bold"><Lock size={10}/> Subscription required</span>
              )}
              <button onClick={leave} className="text-[10px] uppercase tracking-widest text-white/60 hover:text-asap-yellow underline">Change zone</button>
            </div>
            {!user.is_founding && !user.subscription_active && (
              <div className="mt-3 p-3 bg-asap-red/20 border border-asap-red text-asap-yellow text-xs">
                <AlertCircle size={14} className="inline mr-1"/> This zone's 7 founding slots are filled. You'll be visible but won't receive dispatches until you subscribe ($29/mo). Contact admin to activate.
              </div>
            )}
          </>
        )}
      </div>

      {/* Business card */}
      <div className="bg-asap-blue/30 backdrop-blur border-2 border-asap-yellow/40 p-4">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-asap-yellow font-bold">Digital Business Card</div>
            <div className="font-display text-lg mt-1 text-asap-yellow">{user?.card_slug ? `/card/${user.card_slug}` : "Not published"}</div>
          </div>
          {!editingCard && <button onClick={() => setEditingCard(true)} data-testid="edit-card-btn" className="p-2 border-2 border-asap-yellow text-asap-yellow hover:bg-asap-yellow hover:text-asap-deep"><Edit2 size={14}/></button>}
        </div>
        {editingCard ? (
          <div className="mt-3 space-y-2">
            <textarea data-testid="card-bio" value={card.bio} onChange={(e) => setCard({ ...card, bio: e.target.value })} rows={3} placeholder="Tell customers about you…" className="w-full px-3 py-2 bg-white/10 border border-asap-yellow/40 outline-none text-sm text-white placeholder:text-white/40"/>
            <input data-testid="card-photo" value={card.photo_url} onChange={(e) => setCard({ ...card, photo_url: e.target.value })} placeholder="Photo URL (optional)" className="w-full px-3 py-2 bg-white/10 border border-asap-yellow/40 outline-none text-sm text-white placeholder:text-white/40"/>
            <div className="flex gap-2">
              <button onClick={saveCard} data-testid="save-card" className="px-3 py-1 bg-asap-yellow text-asap-deep font-bold uppercase text-xs">Save</button>
              <button onClick={() => setEditingCard(false)} className="px-3 py-1 border border-white/40 text-white/70 text-xs">Cancel</button>
            </div>
          </div>
        ) : user?.card_slug ? (
          <a href={`/card/${user.card_slug}`} target="_blank" rel="noreferrer" data-testid="open-card" className="mt-3 inline-flex items-center gap-1 px-3 py-2 bg-asap-yellow text-asap-deep font-bold uppercase text-xs hover:bg-asap-orange transition">
            View card <ExternalLink size={12}/>
          </a>
        ) : (
          <p className="mt-2 text-white/60 text-xs">Click edit to publish your shareable card with QR code.</p>
        )}
        {msg && <div className="mt-2 text-emerald-300 text-xs font-bold">{msg}</div>}
        {err && <div className="mt-2 text-asap-red text-xs font-bold">{err}</div>}
      </div>
    </div>
  );
}
